import React, { useState } from 'react';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { ITopOverdueData } from 'api/mutations/extractDelayDriverCsv.mutation';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import ErrorBoundary from 'components/common/ErrorBoundary/ErrorBoundary';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import Icon from 'components/common/Icons/icon';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import NoData from 'components/common/NoData/noData';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { CustomPagination } from 'components/common/pagination/customPagination';
import { TitleWithCounter } from 'components/common/titleWithCounter/titleWithCounter';

import { CriticalAnalysisDelayDriversFilter } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramCriticalAnalysis/criticalAnalysisDelayDriversFilter';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/style/table.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IRenderCriticalAnalysisDelayDrivers } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { CustomizedRoundIcon, CustomizedTooltip } from 'components/common';
import { useRenderCriticalAnalysisDelayDriversLogic } from 'components/widgets/CriticalActivitiesAndDelayDrivers/RenderCriticalAnalysisDelayDrivers.logic';
import { useCriticalActivitiesAndDelayDriversProvider } from 'components/widgets/CriticalActivitiesAndDelayDrivers/RenderCriticalAnalysisDelayDrivers.provider';
import {
    columnsV8,
    initialState,
} from 'components/widgets/CriticalActivitiesAndDelayDrivers/RenderCriticalAnalysisDelayDrivers.utils';

const title = 'Critical Activities & Delay Drivers';
const id = 'activitiesDelayDrivers';
const componentKey = 'topCriticalActivitiesDelayDrivers';

const RenderCriticalAnalysisDelayDrivers = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
}: IRenderCriticalAnalysisDelayDrivers) => {
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
    });

    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    const { ui, api } = useCriticalActivitiesAndDelayDriversProvider({
        widgetId,
        latestVersionId,
        contractId,
        projectId,
        globalFilterData,
        localFilters,
        table,
        componentKey,
    });

    const { logic } = useRenderCriticalAnalysisDelayDriversLogic({
        title,
        subTitle,
        api,
        ui,
        table,
    });

    return (
        <ErrorBoundary>
            <WidgetWithTitle
                id={id}
                title={<TitleWithCounter title={title} counter={api.getDelayedDrivers.data?.numOfRows} />}
                tooltip={api.getDelayedDrivers.data?.info}
                titleFilters={[
                    editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                    widgetId && ui.pills.length > 0 ? (
                        <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                    ) : (
                        <CriticalAnalysisDelayDriversFilter
                            key={'filter'}
                            handleOnSearch={logic.handleOnSearchSubmitted}
                            handleDateFilter={logic.handleDate}
                            months={logic.months}
                            dateFilter={ui.state.dateFilter}
                            handleStatusType={logic.handleStatusType}
                            statusFilter={ui.state.statusFilter}
                            handleStatus={logic.handleStatus}
                            typeFilter={ui.state.typeFilter}
                            handleCriticalPath={logic.handleCriticalPath}
                            isCriticalPath={ui.state.isCriticalPath}
                            handleDelayDriver={logic.handleDelayDriver}
                            isDelayDriver={ui.state.isDelayDriver}
                        />
                    ),
                ]}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <CustomizedTooltip
                        key={'CustomizTableColumns'}
                        tooltipPosition={TooltipPosition.Top}
                        tooltipContent={'Customize table columns'}
                        triggerElement={
                            <CustomizedRoundIcon enableHover={true} onClick={() => table.setModalColumnsOpen(true)}>
                                <Icon name={'tune-filter'} />
                            </CustomizedRoundIcon>
                        }
                    />,
                    <AddWidget
                        key={'AddWidget'}
                        componentKey={componentKey}
                        title={title}
                        projectId={ui.state.projectId}
                        contractId={ui.state.contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        callBack={table.setWidgetColumns}
                    />,
                    <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                    <ExportDropdown
                        key={'export'}
                        title={title}
                        handleAPICsvExport={logic.handleAPICsvExport}
                        subTitle={subTitle}
                    />,
                ]}
            >
                <div data-testid={'table-container'} className={classes.tableContainer}>
                    {table.updatedInitialState && (
                        <>
                            <TableV8<ITopOverdueData>
                                data={logic.filteredRows}
                                columns={columnsV8}
                                isLoading={api.getDelayedDrivers.isLoading || api.getDelayedDrivers.isFetching}
                                noData={<NoData>No activities match the applied filter</NoData>}
                                sorting={table.sorting}
                                setSorting={table.setSorting}
                                columnVisibility={table.columnVisibility}
                                setColumnVisibility={table.setColumnVisibility}
                                columnOrder={table.columnOrder}
                                setColumnOrder={table.setColumnOrder}
                                rowSelection={table.rowSelection}
                                setRowSelection={table.setRowSelection}
                            />

                            <div className={classes.pagination}>
                                <CustomPagination
                                    totalPages={table.totalPages}
                                    pagination={table.pagination}
                                    setPagination={table.setPagination}
                                    isDisabled={api.getDelayedDrivers.isFetching}
                                />
                            </div>
                        </>
                    )}
                </div>

                <CustomTableColumnsModal
                    onHideModal={() => table.setModalColumnsOpen(false)}
                    showModal={table.modalColumnsOpen}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    tableInstance={table.tableInstance}
                    initialState={initialState}
                    columns={columnsV8}
                />
            </WidgetWithTitle>
        </ErrorBoundary>
    );
};

export default React.memo(RenderCriticalAnalysisDelayDrivers);
